
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type CompanyInfo = {
  id: string;
  name: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  risk_rating_agency_id?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  fca_number?: string | null;
  join_code?: string | null;
  companyId?: string; // Added for backward compatibility
};

export function useCompanyInfo() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["company-info", user?.id],
    queryFn: async (): Promise<CompanyInfo | null> => {
      if (!user) return null;

      try {
        console.log("Fetching company info for user:", user.id);
        
        // First get the user's profile to find their company ID
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("company_id")
          .eq("id", user.id)
          .single();

        if (profileError || !profile?.company_id) {
          console.log("No company association found:", profileError?.message || "No company ID");
          return null;
        }

        // Now fetch the company details
        const { data: company, error: companyError } = await supabase
          .from("companies")
          .select("id, name, logo_url, primary_color, secondary_color, risk_rating_agency_id, contact_email, contact_phone, fca_number")
          .eq("id", profile.company_id)
          .single();

        if (companyError) {
          console.error("Error fetching company:", companyError);
          return null;
        }

        // Add companyId property for backward compatibility
        const result = {
          ...company,
          companyId: company.id
        } as CompanyInfo;

        console.log("Company info fetched:", result);
        return result;
      } catch (err) {
        console.error("Exception in useCompanyInfo:", err);
        return null;
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}
